import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

function TeacherReport() {

  const storedSession = localStorage.getItem('currentSessionId') || '';
  const [sessionId] = useState(storedSession);

  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterTeacherType, setFilterTeacherType] = useState('');

  useEffect(() => {
    if (!sessionId) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeachers?sessionId=${sessionId}`)
      .then((res) => {
        setTeachers(res.data);
        setFilteredTeachers(res.data.filter(t => t.status === 'active'));
      })
      .catch((err) => console.error('Error fetching teachers:', err));
  }, [sessionId]);


  const handleSearch = () => {
    let data = teachers.filter(t => t.status === 'active');

    if (searchTerm) {
      data = data.filter(t =>
        `${t.firstName} ${t.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory) data = data.filter(t => t.category === filterCategory);
    if (filterGender) data = data.filter(t => t.gender === filterGender);
    if (filterTeacherType)
      data = data.filter(t => t.teacherType === filterTeacherType);

    setFilteredTeachers(data);
  };


  const totalTeachers = teachers.length;

  const totalActive = useMemo(
    () => teachers.filter(t => t.status === 'active').length,
    [teachers]
  );

  const totalInactive = useMemo(
    () => teachers.filter(t => t.status !== 'active').length,
    [teachers]
  );


  const exportToExcel = () => {
    const data = filteredTeachers.map((t, i) => ({
      'Sr No': i + 1,
      Name: `${t.firstName} ${t.lastName}`,
      Gender: t.gender,
      Category: t.category,
      'Teacher Type': t.teacherType,
      Subjects: t.subjectYouTeach?.join(', ') || '',
      'Class Incharge': t.classIncharge || '',
      Session: t.session
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Teachers');
    XLSX.writeFile(wb, 'Teacher_Report.xlsx');
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-100 p-4 rounded shadow">
            <h4 className="text-green-700 font-semibold">Total Teachers</h4>
            <p className="text-2xl font-bold text-green-800">{totalTeachers}</p>
          </div>
          <div className="bg-gray-100 p-4 rounded shadow">
            <h4 className="text-gray-700 font-semibold">Inactive Teachers</h4>
            <p className="text-2xl font-bold text-gray-800">{totalInactive}</p>
          </div>
          <div className="bg-green-100 p-4 rounded">
            <h4 className="text-blue-700 font-semibold">Active Teachers</h4>
            <p className="text-2xl font-bold text-blue-800">{totalActive}</p>
          </div>


        </div>

        {/* Filters */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Filters</h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="border rounded px-3 py-2"
            />

            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Categories</option>
              <option value="General">General</option>
              <option value="SC">SC</option>
              <option value="ST">ST</option>
              <option value="OBC">OBC</option>
              <option value="Minority">Minority</option>
            </select>

            <select
              value={filterGender}
              onChange={e => setFilterGender(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Others">Others</option>
            </select>

            <select
              value={filterTeacherType}
              onChange={e => setFilterTeacherType(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Teacher Types</option>
              <option value="PGT">PGT</option>
              <option value="TGT">TGT</option>
              <option value="PRT">PRT</option>
              <option value="NTT">NTT</option>
              <option value="PET">PET</option>
              <option value="Librarian">Librarian</option>
            </select>

            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Teacher List</h3>
            <button
              onClick={exportToExcel}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Export to Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-4 py-2">Sr</th>
                  <th className="border px-4 py-2">Name</th>
                  <th className="border px-4 py-2">Subjects</th>
                  <th className="border px-4 py-2">Class Incharge</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.length ? (
                  filteredTeachers.map((t, i) => (
                    <tr key={t._id} className="text-center hover:bg-gray-100">
                      <td className="border px-4 py-2">{i + 1}</td>
                      <td className="border px-4 py-2">
                        {t.firstName} {t.lastName}
                      </td>
                      <td className="border px-4 py-2">
                        {t.subjectYouTeach?.join(', ')}
                      </td>
                      <td className="border px-4 py-2">{t.classIncharge}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-6 text-gray-500">
                      No teachers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

export default TeacherReport;
